import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Container,
    Button,
    CircularProgress,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    FileDownload as FileDownloadIcon,
    CurrencyRupee as CurrencyRupeeIcon,
    Receipt as ReceiptIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Layout from '../../components/layout/Layout';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';
import gstReportService from '../../services/gstReportService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import * as XLSX from 'xlsx';

const GSTR2Report = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState({
        rows: [],
        summary: null,
        taxRates: []
    });

    // Default to current month
    const getCurrentMonthDates = () => {
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        const endDate = new Date();
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    };

    const [startDate, setStartDate] = useState(getCurrentMonthDates().startDate);
    const [endDate, setEndDate] = useState(getCurrentMonthDates().endDate);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isExporting, setIsExporting] = useState(false);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return format(new Date(dateString), 'dd MMM yyyy');
        } catch (e) {
            return dateString;
        }
    };

    // Fetch report data
    const fetchReportData = async () => {
        try {
            setLoading(true);
            const response = await gstReportService.getGSTR2Report(startDate, endDate);
            setReportData(response);
        } catch (error) {
            console.error('Error fetching GSTR2 report:', error);
            toast.error('Failed to load GSTR2 report');
        } finally {
            setLoading(false);
        }
    };

    // Load report on mount and when dates change
    useEffect(() => {
        fetchReportData();
    }, [startDate, endDate]);

    // Export to Excel
    const handleExport = async () => {
        try {
            setIsExporting(true);

            // Prepare data for export
            const exportData = reportData.rows.map(row => {
                const rowData = {
                    'Date': formatDate(row.date),
                    'Invoice No': row.invoiceNumber,
                    'Supplier': row.customerName,
                    'Taxable Amount': row.taxableAmount || 0,
                };

                // Add dynamic tax columns (use tax name as key)
                reportData.taxRates.forEach(taxRate => {
                    const taxName = taxRate.name;
                    const taxAmount = row.taxAmounts && row.taxAmounts[taxName]
                        ? row.taxAmounts[taxName]
                        : 0;
                    rowData[taxRate.name] = taxAmount;
                });

                rowData['Total GST'] = row.totalGST || 0;
                rowData['Total Amount'] = row.totalAmount || 0;

                return rowData;
            });

            // Add summary row
            if (reportData.summary) {
                const summaryRow = {
                    'Date': 'TOTAL',
                    'Invoice No': '',
                    'Supplier': '',
                    'Taxable Amount': reportData.summary.totalTaxableAmount || 0,
                };

                reportData.taxRates.forEach(taxRate => {
                    const taxName = taxRate.name;
                    const taxAmount = reportData.summary.totalTaxAmounts && reportData.summary.totalTaxAmounts[taxName]
                        ? reportData.summary.totalTaxAmounts[taxName]
                        : 0;
                    summaryRow[taxRate.name] = taxAmount;
                });

                summaryRow['Total GST'] = reportData.summary.totalGST || 0;
                summaryRow['Total Amount'] = reportData.summary.totalAmount || 0;

                exportData.push(summaryRow);
            }

            // Create workbook
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'GSTR2 Report');

            // Generate filename
            const filename = `GSTR2_Report_${startDate}_to_${endDate}.xlsx`;

            // Save file
            XLSX.writeFile(wb, filename);

            toast.success('Report exported successfully');
        } catch (error) {
            console.error('Error exporting report:', error);
            toast.error('Failed to export report');
        } finally {
            setIsExporting(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Filter rows by invoice number or supplier name (single search field)
    const filteredRows = reportData.rows.filter(row => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase().trim();
        const matchesInvoiceNo = row.invoiceNumber && row.invoiceNumber.toLowerCase().includes(query);
        const matchesSupplierName = row.customerName && row.customerName.toLowerCase().includes(query);
        return matchesInvoiceNo || matchesSupplierName;
    });

    // Paginated rows
    const paginatedRows = filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Reset page when search changes
    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    return (
        <Layout>
            <Container maxWidth="xl" sx={reportPageSx.container}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={reportPageSx.breadcrumbs}>
                    <Link component={RouterLink} to="/reports" color="inherit">
                        Reports
                    </Link>
                    <Typography color="text.primary">GSTR-2</Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                    <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, m: 0 }}>
                        GSTR-2 Report
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={isExporting || loading || reportData.rows.length === 0}
                    >
                        {isExporting ? 'Exporting...' : 'Export to Excel'}
                    </Button>
                </Box>

                <Card sx={reportPageSx.filterCard}>
                    <CardContent sx={reportPageSx.filterCardContent}>
                        <Grid
                            container
                            spacing={reportPageSx.filterGrid.spacing}
                            alignItems="flex-end"
                            sx={{ flexWrap: { xs: 'wrap', lg: 'nowrap' } }}
                        >
                            <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    size="small"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    size="small"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={5} sx={{ minWidth: 0 }}>
                                <TextField
                                    fullWidth
                                    label="Search invoice or supplier"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Invoice # or supplier name"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={fetchReportData}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                                    sx={reportPageSx.filterButton}
                                >
                                    Generate
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {reportData.summary && (
                    <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={4}>
                            <ReportMetricCard
                                label="Purchase Invoices"
                                value={loading ? '…' : filteredRows.length}
                                icon={<ReceiptIcon />}
                                accentColor="primary.main"
                            />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <ReportMetricCard
                                label="Taxable Amount"
                                value={loading ? '…' : formatCurrency(reportData.summary.totalTaxableAmount)}
                                icon={<CurrencyRupeeIcon />}
                                accentColor="warning.main"
                                valueColor="warning.main"
                            />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <ReportMetricCard
                                label="Total Amount"
                                value={loading ? '…' : formatCurrency(reportData.summary.totalAmount)}
                                icon={<CurrencyRupeeIcon />}
                                accentColor="success.main"
                                valueColor="success.main"
                            />
                        </Grid>
                    </Grid>
                )}

                <Card sx={reportPageSx.sectionCard}>
                    <CardContent sx={{ ...reportPageSx.sectionCardContent, p: filteredRows.length ? 0 : undefined, '&:last-child': { pb: filteredRows.length ? 0 : undefined } }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : filteredRows.length === 0 ? (
                            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                                {reportData.rows.length === 0
                                    ? 'No data available for the selected date range'
                                    : 'No records found matching your search criteria'}
                            </Typography>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Invoice No</TableCell>
                                                <TableCell>Supplier</TableCell>
                                                <TableCell align="right">Taxable Amount</TableCell>
                                                {reportData.taxRates.map((taxRate) => (
                                                    <TableCell key={taxRate.rate} align="right">
                                                        {taxRate.name}
                                                    </TableCell>
                                                ))}
                                                <TableCell align="right">Total GST</TableCell>
                                                <TableCell align="right">Total Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedRows.map((row, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell>{formatDate(row.date)}</TableCell>
                                                    <TableCell>{row.invoiceNumber}</TableCell>
                                                    <TableCell>{row.customerName}</TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(row.taxableAmount)}
                                                    </TableCell>
                                                    {/* Dynamic tax columns */}
                                                    {reportData.taxRates.map((taxRate) => {
                                                        const taxName = taxRate.name;
                                                        const taxAmount = row.taxAmounts && row.taxAmounts[taxName]
                                                            ? row.taxAmounts[taxName]
                                                            : 0;
                                                        return (
                                                            <TableCell key={taxRate.name} align="right">
                                                                {taxAmount > 0 ? formatCurrency(taxAmount) : '-'}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell align="right">
                                                        {formatCurrency(row.totalGST)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(row.totalAmount)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            {/* Summary Row */}
                                            {reportData.summary && (
                                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                    <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                                                        <strong>TOTAL</strong>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                        <strong>{formatCurrency(reportData.summary.totalTaxableAmount)}</strong>
                                                    </TableCell>
                                                    {/* Dynamic tax columns summary */}
                                                    {reportData.taxRates.map((taxRate) => {
                                                        const taxName = taxRate.name;
                                                        const taxAmount = reportData.summary.totalTaxAmounts && reportData.summary.totalTaxAmounts[taxName]
                                                            ? reportData.summary.totalTaxAmounts[taxName]
                                                            : 0;
                                                        return (
                                                            <TableCell key={taxRate.name} align="right" sx={{ fontWeight: 'bold' }}>
                                                                <strong>{taxAmount > 0 ? formatCurrency(taxAmount) : '-'}</strong>
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                        <strong>{formatCurrency(reportData.summary.totalGST)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                        <strong>{formatCurrency(reportData.summary.totalAmount)}</strong>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    size="small"
                                    count={filteredRows.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Layout>
    );
};

export default GSTR2Report;
